import { UserFormValues } from "@/api/types";
import { createUser, deleteUser, getUser, getUsers, updateUser } from "@/api/user-api";
import {
    useCollectionData,
    useMutation,
    usePaginatedData,
    buildCacheKey,
} from "@/hooks/use-api";
import { User } from "@/types/user";

// Constants
const USERS_ENDPOINT = "/users";

// Hook for getting all users with pagination and search
export function useUsers(params?: {
    page?: number;
    size?: number;
    searchTerm?: string;
}) {
    const { page = 0, size = 20, searchTerm } = params || {};
    const searchTermTrimmed = searchTerm?.trim() || "";

    const cacheKey = buildCacheKey(USERS_ENDPOINT, {
        page,
        size,
        searchTerm: searchTermTrimmed || undefined,
    });

    return usePaginatedData(
        cacheKey,
        () => getUsers({ page, size, searchTerm: searchTermTrimmed || null }),
        {
            refreshInterval: searchTermTrimmed ? 0 : 30000, // Don't auto-refresh search results
            revalidateOnFocus: !searchTermTrimmed, // Don't refetch on focus during search
        }
    );
}

// Hook for getting a single user
export function useUser(id: string | null) {
    const cacheKey = id ? `${USERS_ENDPOINT}/${id}` : null;

    return useCollectionData<User>(
        cacheKey,
        id ? () => getUser(id) : () => Promise.resolve({ data: null } as any),
        {
            refreshInterval: 0,
            revalidateOnFocus: true,
        }
    );
}

// Hook for creating a user
export function useCreateUser() {
    return useMutation(createUser, {
        onSuccess: (response) => {
            console.log("User created successfully:", response.data);
        },
        onError: (error) => {
            console.error("Error creating user:", error);
        },
        invalidateKeys: [USERS_ENDPOINT],
    });
}

// Hook for updating a user
export function useUpdateUser() {
    return useMutation(
        ({ id, data }: { id: string; data: UserFormValues }) =>
            updateUser(id, data),
        {
            onSuccess: (response, variables) => {
                console.log("User updated successfully:", response.data);
            },
            onError: (error) => {
                console.error("Error updating user:", error);
            },
            invalidateKeys: [USERS_ENDPOINT],
        }
    );
}

// Hook for deleting a customer
export function useDeleteUser() {
    return useMutation(deleteUser, {
        onSuccess: (response) => {
            console.log("User deleted successfully:", response.data);
        },
        onError: (error) => {
            console.error("Error deleting user:", error);
        },
        invalidateKeys: [USERS_ENDPOINT],
    });
}


